import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { formatDuration } from '../../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { toast } from 'sonner';
import { format, parseISO, addDays, startOfWeek } from 'date-fns';
import { 
  ClipboardCheck,
  Search,
  Check,
  X,
  Clock,
  ChevronDown,
  ChevronRight,
  Download,
  Send,
  AlertCircle,
  Users,
  Calendar,
  TrendingUp,
  Filter,
  CheckCircle2,
  XCircle,
  Bell
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminApprovals() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // State
  const [timesheets, setTimesheets] = useState([]);
  const [stats, setStats] = useState({
    this_week_pending: 0,
    this_week_approved: 0,
    not_submitted: 0,
    avg_approval_time_hours: 0,
    all_pending: 0
  });
  const [notSubmitted, setNotSubmitted] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [activeTab, setActiveTab] = useState('submitted');
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [timesheetDetails, setTimesheetDetails] = useState({});
  
  // Modals
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [bulkApproveModalOpen, setBulkApproveModalOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedReminders, setSelectedReminders] = useState([]);

  // Manual time approvals state
  const [manualEntries, setManualEntries] = useState([]);
  const [manualStats, setManualStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [manualFilter, setManualFilter] = useState('pending_approval');
  const [manualSearch, setManualSearch] = useState('');
  const [manualSelectedIds, setManualSelectedIds] = useState([]);
  const [manualRejectEntry, setManualRejectEntry] = useState(null);
  const [manualRejectComment, setManualRejectComment] = useState('');

  // Check if current week's timesheets are restricted from approval
  // Approval restriction removed — managers can approve/reject any day
  const isApprovalRestricted = () => false;

  // Global check: is today a restricted day for current-week approvals?
  // (kept for potential future use, but banner removed per user request)

  // Access check
  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('You do not have access to this page');
    }
  }, [user, navigate]);

  // Fetch data
  const fetchTimesheets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('status', activeTab); // Always send status, including 'all'
      if (searchQuery) params.append('search', searchQuery);
      if (departmentFilter !== 'all') params.append('department', departmentFilter);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);

      const res = await fetch(`${API_URL}/api/admin/approvals?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTimesheets(data);
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
    }
  }, [token, activeTab, searchQuery, departmentFilter, dateRange]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/approvals/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  const fetchNotSubmitted = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/approvals/not-submitted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setNotSubmitted(data);
    } catch (error) {
      console.error('Failed to fetch not submitted:', error);
    }
  }, [token]);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDepartments(data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  }, [token]);

  const fetchManualEntries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (manualFilter !== 'all') params.append('status', manualFilter);
      if (manualSearch) params.append('search', manualSearch);
      const res = await fetch(`${API_URL}/api/admin/manual-time-approvals?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setManualEntries(data.entries || []);
      setManualStats(data.stats || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Failed to fetch manual entries:', error);
    }
  }, [token, manualFilter, manualSearch]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTimesheets(), fetchStats(), fetchNotSubmitted(), fetchDepartments(), fetchManualEntries()]);
      setLoading(false);
    };
    if (token) loadData();
  }, [token, fetchTimesheets, fetchStats, fetchNotSubmitted, fetchDepartments, fetchManualEntries]);

  // Fetch timesheet detail for preview
  const fetchTimesheetDetail = async (timesheetId) => {
    if (timesheetDetails[timesheetId]) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/approvals/${timesheetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTimesheetDetails(prev => ({ ...prev, [timesheetId]: data }));
    } catch (error) {
      console.error('Failed to fetch timesheet detail:', error);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = async (timesheetId) => {
    if (!expandedRows[timesheetId]) {
      await fetchTimesheetDetail(timesheetId);
    }
    setExpandedRows(prev => ({ ...prev, [timesheetId]: !prev[timesheetId] }));
  };

  // Handlers
  const handleApprove = async () => {
    if (!selectedTimesheet) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/approvals/${selectedTimesheet.id}/approve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to approve');
      }

      toast.success('Timesheet approved');
      setApproveModalOpen(false);
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleReject = async () => {
    if (!selectedTimesheet || rejectReason.length < 10) {
      toast.error('Rejection reason must be at least 10 characters');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/approvals/${selectedTimesheet.id}/reject`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectReason })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to reject');
      }

      toast.success('Timesheet rejected');
      setRejectModalOpen(false);
      setRejectReason('');
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/approvals/bulk/approve`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ timesheet_ids: selectedIds })
      });

      const data = await res.json();
      toast.success(`${data.approved?.length || 0} timesheets approved`);
      setBulkApproveModalOpen(false);
      setSelectedIds([]);
      fetchTimesheets();
      fetchStats();
    } catch (error) {
      toast.error('Failed to bulk approve');
    }
  };

  const handleSendReminders = async () => {
    if (selectedReminders.length === 0) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/approvals/send-reminder`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ employee_ids: selectedReminders })
      });

      const data = await res.json();
      toast.success(`Reminders sent to ${data.sent?.length || 0} employees`);
      setReminderModalOpen(false);
      setSelectedReminders([]);
    } catch (error) {
      toast.error('Failed to send reminders');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);

      const res = await fetch(`${API_URL}/api/admin/approvals/export/csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `approvals_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === timesheets.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(timesheets.map(ts => ts.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Manual time entry handlers
  const handleManualApprove = async (entryId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/manual-time-approvals/${entryId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Manual time entry approved');
      fetchManualEntries();
    } catch { toast.error('Failed to approve entry'); }
  };

  const handleManualReject = async () => {
    if (!manualRejectEntry) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/manual-time-approvals/${manualRejectEntry.id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ comment: manualRejectComment }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Manual time entry rejected');
      setManualRejectEntry(null);
      setManualRejectComment('');
      fetchManualEntries();
    } catch { toast.error('Failed to reject entry'); }
  };

  const handleManualBulkApprove = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/manual-time-approvals/bulk-approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entry_ids: manualSelectedIds }),
      });
      const data = await res.json();
      toast.success(`${data.approved} entries approved`);
      setManualSelectedIds([]);
      fetchManualEntries();
    } catch { toast.error('Failed to bulk approve'); }
  };

  // Helpers
  const formatWeekRange = (weekStart) => {
    try {
      const start = parseISO(weekStart);
      const end = addDays(start, 6);
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    } catch {
      return weekStart;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
      case 'approved': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
      case 'rejected': return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-700';
    }
  };

  const getDaysColor = (days) => {
    if (days === null) return 'text-slate-400';
    if (days <= 1) return 'text-emerald-600';
    if (days <= 3) return 'text-amber-600';
    return 'text-red-600';
  };

  // Get pending timesheets for the current tab
  const pendingTimesheets = timesheets.filter(ts => ts.status === 'submitted');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-approvals-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('approvals.timesheetApprovals')}</h1>
              {stats.all_pending > 0 && (
                <Badge className="bg-amber-500 dark:bg-amber-600 text-white">{stats.all_pending} pending</Badge>
              )}
            </div>
            <p className="text-slate-500 dark:text-slate-400 mt-1">{t('admin.approvalsSubtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && activeTab === 'submitted' && (
              <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-1.5">
                <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">
                  {selectedIds.length} selected
                </span>
                <div className="w-px h-5 bg-slate-300 dark:bg-slate-600" />
                <Button 
                  size="sm"
                  onClick={() => setBulkApproveModalOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 h-8"
                  data-testid="bulk-approve-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button 
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    if (selectedIds.length === 1) {
                      const ts = timesheets.find(t => t.id === selectedIds[0]);
                      setSelectedTimesheet(ts);
                      setRejectModalOpen(true);
                    } else {
                      toast.error('Please reject timesheets one at a time to provide rejection reasons');
                    }
                  }}
                  className="h-8"
                  data-testid="bulk-reject-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button 
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedIds([])}
                  className="h-8 text-slate-500"
                >
                  Clear
                </Button>
              </div>
            )}
            <Button variant="outline" onClick={handleExport} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        {activeTab !== 'manual_time' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.this_week_pending}</p>
                  <p className="text-sm text-slate-500">This Week Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.this_week_approved}</p>
                  <p className="text-sm text-slate-500">This Week Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.avg_approval_time_hours}h</p>
                  <p className="text-sm text-slate-500">Avg Approval Time</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-red-500/50"
            onClick={() => setReminderModalOpen(true)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.not_submitted}</p>
                  <p className="text-sm text-slate-500">Not Yet Submitted</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Filters */}
        {activeTab !== 'manual_time' && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by employee name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="department-filter">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-[140px]"
                  placeholder="Start date"
                />
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-[140px]"
                  placeholder="End date"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="submitted" data-testid="tab-pending">
              Pending
              {stats.all_pending > 0 && (
                <Badge className="ml-2 bg-amber-500 dark:bg-amber-600 text-white text-xs">{stats.all_pending}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="manual_time" data-testid="tab-manual-time">
              Manual Time
              {manualStats.pending > 0 && (
                <Badge className="ml-2 bg-violet-500 dark:bg-violet-600 text-white text-xs">{manualStats.pending}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Timesheets List - hidden when Manual Time tab is active */}
          {activeTab !== 'manual_time' && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="approvals-table">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      {activeTab === 'submitted' && (
                        <th className="px-4 py-3 text-left">
                          <Checkbox
                            checked={selectedIds.length === timesheets.length && timesheets.length > 0}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('people.employee')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('approvals.weekEnding')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('approvals.totalHours')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('approvals.submittedOn')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('approvals.daysPending')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('common.status')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('timeOff.actionedBy')}</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {timesheets.map((ts) => (
                      <React.Fragment key={ts.id}>
                        <tr 
                          className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                          data-testid={`timesheet-row-${ts.id}`}
                        >
                          {activeTab === 'submitted' && (
                            <td className="px-4 py-3">
                              <Checkbox
                                checked={selectedIds.includes(ts.id)}
                                onCheckedChange={() => toggleSelect(ts.id)}
                              />
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <button
                              onClick={() => toggleRowExpansion(ts.id)}
                              className="flex items-center gap-3 text-left"
                            >
                              {expandedRows[ts.id] ? (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium text-sm overflow-hidden">
                                {ts.employee?.profile_photo ? (
                                  <img src={ts.employee.profile_photo} alt={ts.employee.name} className="w-full h-full object-cover" />
                                ) : (
                                  ts.employee?.name?.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">{ts.employee?.name}</p>
                                <p className="text-xs text-slate-500">{ts.employee?.department || <span className="italic">Not assigned</span>}</p>
                              </div>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {formatWeekRange(ts.week_start)}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                            {ts.total_hours?.toFixed(1) || '0'}h
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {ts.submitted_at ? format(parseISO(ts.submitted_at), 'MMM d, h:mm a') : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-medium ${getDaysColor(ts.days_since_submitted)}`}>
                              {ts.days_since_submitted !== null ? `${ts.days_since_submitted}d` : '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={getStatusBadge(ts.status)}>
                              {ts.status === 'submitted' ? 'Pending' : ts.status?.charAt(0).toUpperCase() + ts.status?.slice(1)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                            {ts.status === 'approved' ? ts.approver_name || '-' : 
                             ts.status === 'rejected' ? ts.rejector_name || '-' : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {ts.status === 'submitted' && (
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  disabled={isApprovalRestricted(ts.week_start)}
                                  onClick={() => {
                                    if (isApprovalRestricted(ts.week_start)) {
                                      toast.error("Current week's timesheets can only be approved on Friday, Saturday, or Sunday.");
                                      return;
                                    }
                                    setSelectedTimesheet(ts);
                                    setApproveModalOpen(true);
                                  }}
                                  data-testid={`approve-btn-${ts.id}`}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  disabled={isApprovalRestricted(ts.week_start)}
                                  onClick={() => {
                                    if (isApprovalRestricted(ts.week_start)) {
                                      toast.error("Current week's timesheets can only be rejected on Friday, Saturday, or Sunday.");
                                      return;
                                    }
                                    setSelectedTimesheet(ts);
                                    setRejectModalOpen(true);
                                  }}
                                  data-testid={`reject-btn-${ts.id}`}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                            {ts.status === 'rejected' && ts.rejection_note && (
                              <span className="text-sm text-red-600" title={ts.rejection_note}>
                                Reason: {ts.rejection_note.substring(0, 30)}...
                              </span>
                            )}
                          </td>
                        </tr>
                        {/* Expanded Row - Timesheet Preview */}
                        {expandedRows[ts.id] && timesheetDetails[ts.id] && (
                          <tr>
                            <td colSpan={activeTab === 'submitted' ? 8 : 7} className="bg-slate-50 dark:bg-slate-800/30 px-4 py-4">
                              <div className="pl-8">
                                <h4 className="font-medium text-sm mb-3">Timesheet Details</h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm border rounded-lg overflow-hidden">
                                    <thead className="bg-slate-100 dark:bg-slate-700">
                                      <tr>
                                        <th className="px-3 py-2 text-left font-medium">Project</th>
                                        {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                                          const date = addDays(parseISO(ts.week_start), dayOffset);
                                          return (
                                            <th key={dayOffset} className="px-3 py-2 text-center font-medium w-16">
                                              {format(date, 'EEE')}
                                              <br />
                                              <span className="text-xs text-slate-500">{format(date, 'd')}</span>
                                            </th>
                                          );
                                        })}
                                        <th className="px-3 py-2 text-center font-medium">Total</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                      {timesheetDetails[ts.id].grid_data?.map((row, idx) => {
                                        const rowTotal = Object.values(row.days || {}).reduce((sum, h) => sum + (h || 0), 0);
                                        return (
                                          <tr key={idx}>
                                            <td className="px-3 py-2">
                                              <div className="flex items-center gap-2">
                                                <div 
                                                  className="w-2 h-2 rounded-full" 
                                                  style={{ backgroundColor: row.project?.color || '#94a3b8' }}
                                                />
                                                {row.project?.name}
                                              </div>
                                            </td>
                                            {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                                              const date = format(addDays(parseISO(ts.week_start), dayOffset), 'yyyy-MM-dd');
                                              const hours = row.days?.[date] || 0;
                                              return (
                                                <td key={dayOffset} className="px-3 py-2 text-center">
                                                  {hours > 0 ? hours.toFixed(1) : '-'}
                                                </td>
                                              );
                                            })}
                                            <td className="px-3 py-2 text-center font-medium">
                                              {rowTotal.toFixed(1)}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      {(!timesheetDetails[ts.id].grid_data || timesheetDetails[ts.id].grid_data.length === 0) && (
                                        <tr>
                                          <td colSpan={9} className="px-3 py-4 text-center text-slate-500">
                                            No time entries for this week
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                    <tfoot className="bg-slate-100 dark:bg-slate-700">
                                      <tr>
                                        <td className="px-3 py-2 font-medium">Daily Total</td>
                                        {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                                          const date = format(addDays(parseISO(ts.week_start), dayOffset), 'yyyy-MM-dd');
                                          const dayTotal = (timesheetDetails[ts.id].grid_data || []).reduce(
                                            (sum, row) => sum + (row.days?.[date] || 0), 0
                                          );
                                          return (
                                            <td key={dayOffset} className="px-3 py-2 text-center font-medium">
                                              {dayTotal > 0 ? dayTotal.toFixed(1) : '-'}
                                            </td>
                                          );
                                        })}
                                        <td className="px-3 py-2 text-center font-bold">
                                          {ts.total_hours?.toFixed(1) || '0'}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {timesheets.length === 0 && (
                      <tr>
                        <td colSpan={activeTab === 'submitted' ? 8 : 7} className="px-4 py-12 text-center text-slate-500">
                          No timesheets found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Manual Time Tab Content */}
          <TabsContent value="manual_time">
            <div className="space-y-4">
              {/* Manual Time Filters */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="flex gap-2">
                  {[
                    { value: 'pending_approval', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
                    { value: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
                    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
                    { value: 'all', label: 'All', color: 'bg-slate-100 dark:bg-slate-700 text-slate-700' },
                  ].map(f => (
                    <Button
                      key={f.value}
                      variant={manualFilter === f.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setManualFilter(f.value); setManualSelectedIds([]); }}
                      className={manualFilter === f.value ? '' : ''}
                      data-testid={`manual-filter-${f.value}`}
                    >
                      {f.label}
                      {f.value === 'pending_approval' && manualStats.pending > 0 && (
                        <Badge className="ml-1.5 bg-amber-500 dark:bg-amber-600 text-white text-xs">{manualStats.pending}</Badge>
                      )}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {manualSelectedIds.length > 0 && manualFilter === 'pending_approval' && (
                    <Button size="sm" onClick={handleManualBulkApprove} className="bg-emerald-600 hover:bg-emerald-700" data-testid="manual-bulk-approve-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve {manualSelectedIds.length}
                    </Button>
                  )}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name..."
                      value={manualSearch}
                      onChange={(e) => setManualSearch(e.target.value)}
                      className="pl-9 w-56 h-9"
                      data-testid="manual-search-input"
                    />
                  </div>
                </div>
              </div>

              {/* Manual Entries Table */}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="manual-approvals-table">
                      <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          {manualFilter === 'pending_approval' && (
                            <th className="px-4 py-3 text-left">
                              <Checkbox
                                checked={manualSelectedIds.length === manualEntries.length && manualEntries.length > 0}
                                onCheckedChange={(checked) => {
                                  setManualSelectedIds(checked ? manualEntries.map(e => e.id) : []);
                                }}
                                data-testid="manual-select-all"
                              />
                            </th>
                          )}
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Employee</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Time Range</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Duration</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Project</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {manualEntries.length === 0 ? (
                          <tr>
                            <td colSpan={manualFilter === 'pending_approval' ? 9 : 8} className="px-4 py-12 text-center text-slate-400">
                              <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                              No manual time entries found
                            </td>
                          </tr>
                        ) : manualEntries.map(entry => {
                          const startDate = new Date(entry.start_time);
                          const endDate = entry.end_time ? new Date(entry.end_time) : null;
                          return (
                            <tr key={entry.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30" data-testid={`manual-entry-${entry.id}`}>
                              {manualFilter === 'pending_approval' && (
                                <td className="px-4 py-3">
                                  <Checkbox
                                    checked={manualSelectedIds.includes(entry.id)}
                                    onCheckedChange={() => setManualSelectedIds(prev =>
                                      prev.includes(entry.id) ? prev.filter(i => i !== entry.id) : [...prev, entry.id]
                                    )}
                                  />
                                </td>
                              )}
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center text-xs font-bold">
                                    {entry.user?.name?.charAt(0) || '?'}
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-slate-900 dark:text-white">{entry.user?.name}</div>
                                    <div className="text-xs text-slate-400">{entry.user?.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                                {format(startDate, 'MMM d, yyyy')}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                                {format(startDate, 'hh:mm a')} {endDate ? `- ${format(endDate, 'hh:mm a')}` : ''}
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-white">
                                {formatDuration(entry.duration)}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 max-w-[180px] truncate">
                                {entry.description || '-'}
                              </td>
                              <td className="px-4 py-3">
                                {entry.project ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                                    <span className="w-2 h-2 rounded-full" style={{ background: entry.project.color || '#6366f1' }} />
                                    {entry.project.name}
                                  </span>
                                ) : <span className="text-xs text-slate-400">-</span>}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  entry.approval_status === 'pending_approval' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300' :
                                  entry.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' :
                                  'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                                }`}>
                                  {entry.approval_status === 'pending_approval' ? 'Pending' :
                                   entry.approval_status === 'approved' ? 'Approved' : 'Rejected'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {entry.approval_status === 'pending_approval' ? (
                                  <div className="flex items-center justify-end gap-1.5">
                                    <Button size="sm" variant="ghost" className="h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-900/30"
                                      onClick={() => handleManualApprove(entry.id)} data-testid={`manual-approve-${entry.id}`}>
                                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:bg-red-900/30"
                                      onClick={() => setManualRejectEntry(entry)} data-testid={`manual-reject-${entry.id}`}>
                                      <X className="w-4 h-4 mr-1" /> Reject
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400">
                                    {entry.approval_comment || (entry.approval_status === 'approved' ? 'Approved' : 'Rejected')}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Manual Time Reject Modal */}
        <Dialog open={!!manualRejectEntry} onOpenChange={(open) => { if (!open) { setManualRejectEntry(null); setManualRejectComment(''); }}}>
          <DialogContent data-testid="manual-reject-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <X className="w-5 h-5" />
                Reject Manual Time Entry
              </DialogTitle>
              <DialogDescription>
                Reject entry by <strong>{manualRejectEntry?.user?.name}</strong> — {manualRejectEntry?.description || 'No description'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Rejection Reason (optional)</label>
                <Input
                  value={manualRejectComment}
                  onChange={(e) => setManualRejectComment(e.target.value)}
                  placeholder="Provide a reason..."
                  className="mt-1"
                  data-testid="manual-reject-comment"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setManualRejectEntry(null); setManualRejectComment(''); }}>Cancel</Button>
                <Button variant="destructive" onClick={handleManualReject} data-testid="manual-reject-confirm-btn">Reject Entry</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Approve Modal */}
        <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
          <DialogContent data-testid="approve-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                Approve Timesheet
              </DialogTitle>
              <DialogDescription>
                Approve timesheet for <strong>{selectedTimesheet?.employee?.name}</strong> for week{' '}
                <strong>{selectedTimesheet && formatWeekRange(selectedTimesheet.week_start)}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-lg">
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                  {selectedTimesheet?.total_hours?.toFixed(1) || '0'} hours
                </p>
                <p className="text-sm text-emerald-600 dark:text-emerald-400">Total tracked for this week</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproveModalOpen(false)}>Cancel</Button>
              <Button onClick={handleApprove} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-approve-btn">
                <Check className="w-4 h-4 mr-2" />
                Confirm Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
          <DialogContent data-testid="reject-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="w-5 h-5" />
                Reject Timesheet
              </DialogTitle>
              <DialogDescription>
                Reject timesheet for <strong>{selectedTimesheet?.employee?.name}</strong>. 
                Please provide a reason for rejection.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please explain why this timesheet is being rejected (min 10 characters)..."
                  rows={4}
                  data-testid="reject-reason-input"
                />
                <p className="text-xs text-slate-500">{rejectReason.length}/10 characters minimum</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setRejectModalOpen(false); setRejectReason(''); }}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={handleReject}
                disabled={rejectReason.length < 10}
                data-testid="confirm-reject-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Reject Timesheet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Approve Modal */}
        <Dialog open={bulkApproveModalOpen} onOpenChange={setBulkApproveModalOpen}>
          <DialogContent data-testid="bulk-approve-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                Bulk Approve Timesheets
              </DialogTitle>
              <DialogDescription>
                You are about to approve {selectedIds.length} timesheet(s).
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="max-h-48 overflow-y-auto space-y-2">
                {timesheets.filter(ts => selectedIds.includes(ts.id)).map(ts => (
                  <div key={ts.id} className="flex items-center justify-between p-2 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs">
                        {ts.employee?.name?.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium">{ts.employee?.name}</span>
                    </div>
                    <span className="text-sm text-slate-500">{formatWeekRange(ts.week_start)}</span>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkApproveModalOpen(false)}>Cancel</Button>
              <Button onClick={handleBulkApprove} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-bulk-approve-btn">
                <Check className="w-4 h-4 mr-2" />
                Approve All ({selectedIds.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Reminder Modal */}
        <Dialog open={reminderModalOpen} onOpenChange={setReminderModalOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="reminder-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Send Timesheet Reminders
              </DialogTitle>
              <DialogDescription>
                Select employees to remind about their pending timesheet submissions.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="max-h-64 overflow-y-auto space-y-2">
                {notSubmitted.map(emp => (
                  <label
                    key={emp.id}
                    className="flex items-center gap-3 p-2 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedReminders.includes(emp.id)}
                      onCheckedChange={(checked) => {
                        setSelectedReminders(prev => 
                          checked ? [...prev, emp.id] : prev.filter(id => id !== emp.id)
                        );
                      }}
                    />
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm">
                      {emp.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{emp.name}</p>
                      <p className="text-xs text-slate-500">{emp.department || <span className="italic">Not assigned</span>}</p>
                    </div>
                  </label>
                ))}
                {notSubmitted.length === 0 && (
                  <p className="text-center py-4 text-slate-500">All employees have submitted their timesheets!</p>
                )}
              </div>
              {notSubmitted.length > 0 && (
                <div className="flex justify-between mt-4 pt-4 border-t">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setSelectedReminders(notSubmitted.map(e => e.id))}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setSelectedReminders([])}
                  >
                    Clear All
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setReminderModalOpen(false); setSelectedReminders([]); }}>Cancel</Button>
              <Button 
                onClick={handleSendReminders}
                disabled={selectedReminders.length === 0}
                data-testid="send-reminders-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Reminder ({selectedReminders.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
